import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import type { LLMModel } from "./types";

type Props = {
  model?: LLMModel | null;
  t: (key: string, options?: { defaultValue?: string }) => string;
};

/* ================= CONSTANT ================= */

const ALLOWED_KEYS = new Set([
  "temperature",
  "max_tokens",
  "embedding_dimension",
  "extra_config.context_length",
  "extra_config.max_output_tokens",
  "extra_config.max_input_tokens",
  "extra_config.top_p",
  "extra_config.max_image_size",
  "extra_config.max_images_per_request",
  "extra_config.max_input_characters",
  // "extra_config.supported_formats",
  "extra_config.max_audio_duration_seconds",
  "extra_config.max_audio_size",
  "extra_config.supported_audio_format",
  "extra_config.output_formats",
]);

/* ================= UTILS ================= */

const formatLabel = (key: string): string =>
  key
    .split(".")
    .pop()!
    .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

const normalizeConfig = (data: LLMModel): Record<string, unknown> => {
  const { extra_config = {}, ...rest } = data;

  return {
    ...rest,
    ...Object.fromEntries(
      Object.entries(extra_config).map(([k, v]) => [`extra_config.${k}`, v]),
    ),
  };
};

const isValidValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

/* ================= SUB COMPONENT ================= */

type ConfigItemProps = {
  label: string;
  value: unknown;
};

const ConfigItem: React.FC<ConfigItemProps> = ({ label, value }) => (
  <Box className="px-2 py-1 rounded border border-[var(--border)] bg-[var(--card)] mb-2">
    <Typography className="text-sm font-normal text-[var(--foreground)] break-words">
      {formatValue(value)}
    </Typography>

    <Typography className="text-xs font-normal text-[var(--muted-foreground)]">
      {label}
    </Typography>
  </Box>
);

/* ================= MAIN ================= */

const AICatalogDetailConfigurationsComp: React.FC<Props> = ({ model, t }) => {
  const filtered = useMemo(() => {
    if (!model) return [];

    const normalized = normalizeConfig(model);

    return Object.entries(normalized).filter(
      ([key, value]) => ALLOWED_KEYS.has(key) && isValidValue(value),
    );
  }, [model]);

  if (!filtered.length) return null;

  return (
    <Box className="px-2 py-4">
      <Typography className="text-xs font-medium text-[var(--muted-foreground)] mb-2">
        {t("aiCatalog.configurations")}
      </Typography>

      {filtered.map(([key, value]) => {
        const formattedKey = formatLabel(key);

        return (
          <ConfigItem
            key={key}
            value={value}
            label={t(`modelRegistration.${formattedKey}`, {
              defaultValue: formattedKey,
            })}
          />
        );
      })}
    </Box>
  );
};

export default AICatalogDetailConfigurationsComp;
